/**
 * AccountPlanSection — RC 1.32 foundation, RC 1.34 top-up UX.
 *
 * Rendered inside /account?tab=plan. Shows current plan, status,
 * expiry, and the two allowance buckets. Also hosts the RC 1.34
 * "Add usage" stub flow (package selector → confirmation → activate
 * → refresh) talking to `/api/account/usage-packages*`.
 *
 * Language rules (RC 1.33.C sweep + RC 1.34 reinforcement):
 *   - We say "usage", "included usage", "extra usage", "add usage",
 *     "usage remaining". Never "credits" / "buy credits" /
 *     "credit packs" / "Starter" / "Growth" / "Power".
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Loader2, CircleCheck, TriangleAlert, Plus, ArrowLeft, X,
  ExternalLink, History,
} from 'lucide-react';
import { isStripeTestMode } from '@/lib/stripeMode';
import { useCredits } from '@/lib/creditsContext';
import { getFullyTailoredAppCost } from '@/lib/creditsHelpers';
import Disclaimer from '@/components/Disclaimer';

const PLAN_LABEL = { base: 'Base', focus: 'Focus', flow: 'Flow' };

// RC 1.45.HARMONISE — bundle size per plan (must match backend
// core/pricing.py::PLAN_TIERS included_usage). Used to derive the
// "covers roughly N fully-tailored applications per month" strapline.
// We do not read this off `plan` on this page because `/api/account/plan`
// only returns the user's remaining balance, not the bundle size.
//
// RC 1.48.7B (2026-05-04) — the per-app cost divisor is now read
// live from the creditsContext via `getFullyTailoredAppCost(usageCosts)`;
// we no longer duplicate it as a hardcoded constant. See the
// RC 1.48.7A audit report + `lib/creditsHelpers.js`.
const PLAN_MONTHLY_BUNDLE = { focus: 120, flow: 250 };
const PERIOD_LABEL = {
  monthly: 'Monthly', annual: 'Annual',
};
const STATUS_LABEL = {
  active: 'Active', pending: 'Pending', expired: 'Expired',
};

export default function AccountPlanSection() {
  const [plan,   setPlan]    = useState(null);
  const [loading, setLoading] = useState(true);
  // RC 1.48.7B — live per-app cost derived from the creditsContext.
  // Null while the provider is bootstrapping; renders "—" until
  // then so no stale hardcoded number is ever shown.
  const { usageCosts } = useCredits();
  const perAppCost = getFullyTailoredAppCost(usageCosts);

  // RC 1.34 — Add-usage stub flow state.
  // step: 'idle' | 'select' | 'confirm' | 'activating'
  const [step,     setStep]     = useState('idle');
  const [packages, setPackages] = useState([]);
  const [pkgsLoading, setPkgsLoading] = useState(false);
  const [selected, setSelected] = useState(null);  // package dict

  // RC 1.35.F3 P1.7 — Stripe Customer Portal deeplink state.
  const [portalLoading, setPortalLoading] = useState(false);

  const onManageBilling = useCallback(async () => {
    setPortalLoading(true);
    try {
      const res = await apiFetch('/api/payments/customer-portal', {
        method: 'POST',
        body:   JSON.stringify({}),
      });
      if (!res?.url) throw new Error('Stripe did not return a portal URL.');
      window.location.assign(res.url);
    } catch (err) {
      toast.error(err?.message || 'Unable to open the Stripe billing portal.');
      setPortalLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = await apiFetch('/api/account/plan');
      setPlan(p);
    } catch {
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openSelector = useCallback(async () => {
    setStep('select');
    if (packages.length === 0) {
      setPkgsLoading(true);
      try {
        const r = await apiFetch('/api/account/usage-packages');
        setPackages(Array.isArray(r?.packages) ? r.packages : []);
      } catch (err) {
        toast.error(err?.message || 'Unable to load credit packages.');
        setStep('idle');
      } finally {
        setPkgsLoading(false);
      }
    }
  }, [packages.length]);

  // RC 1.38 — when the user is routed here from
  // /account?tab=plan&addUsage=1 (e.g. the Top Opportunities
  // insufficient-usage modal), pre-open the package selector so the
  // first thing they see is the catalogue. Fires exactly once per
  // mount so navigating away and back doesn't loop.
  const location = useLocation();
  const navigate = useNavigate();
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current) return;
    if (loading)                return;
    const params = new URLSearchParams(location.search);
    if (params.get('addUsage') !== '1') return;
    autoOpenedRef.current = true;
    openSelector();
    // Clean the URL so a refresh / back-button trip doesn't reopen.
    params.delete('addUsage');
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true },
    );
  }, [loading, location.pathname, location.search, navigate, openSelector]);

  const closeFlow = () => {
    setStep('idle');
    setSelected(null);
  };

  const chooseSelected = async (pkg) => {
    setSelected(pkg);
    setStep('confirm');
  };

  const confirmActivate = async () => {
    if (!selected) return;
    setStep('activating');
    try {
      const res = await apiFetch('/api/payments/checkout/topup', {
        method: 'POST',
        body:   JSON.stringify({ package_code: selected.code }),
      });
      if (!res?.checkout_url) {
        throw new Error('Stripe did not return a checkout URL.');
      }
      window.location.assign(res.checkout_url);
    } catch (err) {
      toast.error(err?.message || 'Unable to start Stripe Checkout.');
      setStep('confirm');
    }
  };

  // RC 1.35.E — when the user returns from Stripe Checkout for a
  // top-up (`?session_id=cs_test_...&topup=1`), poll the status
  // endpoint until the webhook (or polling fallback) finalises the
  // session, then refresh the visible plan + balance and show a
  // success toast. Cancellations show a polite toast and clear the
  // URL.
  const pollSessionRef = useRef(false);
  useEffect(() => {
    if (pollSessionRef.current) return;
    if (loading) return;
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const cancelled = params.get('cancelled') === '1';
    if (cancelled) {
      pollSessionRef.current = true;
      toast.error('Top-up cancelled.');
      params.delete('cancelled');
      const search = params.toString();
      navigate(
        { pathname: location.pathname, search: search ? `?${search}` : '' },
        { replace: true },
      );
      return;
    }
    if (!sessionId) return;
    pollSessionRef.current = true;
    (async () => {
      let finalised = false;
      for (let i = 0; i < 30; i++) {
        try {
          const r = await apiFetch(`/api/payments/checkout/status/${sessionId}`);
          if (r?.finalised || r?.status === 'finalised') {
            finalised = true;
            break;
          }
        } catch {
          break;
        }
        await new Promise((res) => setTimeout(res, 2000));
      }
      await load();
      if (finalised) {
        toast.success('Top-up Credits added to your account.');
      } else {
        toast.message('Stripe is still confirming your payment. Refresh in a moment.');
      }
      params.delete('session_id');
      params.delete('topup');
      const search = params.toString();
      navigate(
        { pathname: location.pathname, search: search ? `?${search}` : '' },
        { replace: true },
      );
    })();
  }, [loading, location.pathname, location.search, navigate, load]);

  // RC 1.44.O1.C — Pending-plan auto-reconciliation.
  //
  //   • If `plan.status === 'pending'` AND `pending_since >= 10 min`
  //     ago → silently revert via `/api/account/plan/pending/cancel`
  //     so the Account page never carries a stuck Pending badge.
  //   • If `plan.status === 'pending'` AND `pending_since < 10 min`
  //     → render an inline recovery banner with "Resume payment"
  //     (route to /onboarding/payment, where Stripe Checkout is
  //     re-launched) and "Cancel pending plan" (call the same
  //     endpoint on demand). 10-minute window matches the typical
  //     Stripe Checkout abandonment shape — long enough that a user
  //     who briefly tabbed away can still resume; short enough that
  //     we don't leave dead pending state lying around.
  const [cancellingPending, setCancellingPending] = useState(false);
  const PENDING_RECOVERY_WINDOW_MS = 10 * 60 * 1000;

  const cancelPending = useCallback(async () => {
    setCancellingPending(true);
    try {
      await apiFetch('/api/account/plan/pending/cancel', {
        method: 'POST',
        body:   JSON.stringify({}),
      });
      await load();
    } catch (err) {
      toast.error(err?.message || 'Could not cancel the pending plan.');
    } finally {
      setCancellingPending(false);
    }
  }, [load]);

  const pendingAutoCancelledRef = useRef(false);
  useEffect(() => {
    if (loading) return;
    if (!plan) return;
    if (plan.status !== 'pending') return;
    if (pendingAutoCancelledRef.current) return;

    const sinceIso = plan.pending_since;
    if (!sinceIso) return;
    const sinceMs = new Date(sinceIso).getTime();
    if (Number.isNaN(sinceMs)) return;
    const ageMs = Date.now() - sinceMs;
    if (ageMs < PENDING_RECOVERY_WINDOW_MS) return;

    pendingAutoCancelledRef.current = true;
    (async () => {
      try {
        const res = await apiFetch('/api/account/plan/pending/cancel', {
          method: 'POST',
          body:   JSON.stringify({}),
        });
        if (res?.cancelled) {
          toast.message('Cleared an abandoned plan selection — you\'re back on Base.');
        }
        await load();
      } catch {
        // Fail-quiet — the recovery banner will still surface so the
        // user can cancel manually. We don't toast on background
        // self-heal failures.
      }
    })();
  }, [loading, plan, PENDING_RECOVERY_WINDOW_MS, load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-ndor-ink-muted py-6">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading plan…
      </div>
    );
  }
  if (!plan) {
    return (
      <div className="text-sm text-ndor-ink-muted py-6">
        Plan information is unavailable right now. Please try again shortly.
      </div>
    );
  }

  const isExpired = plan.status === 'expired';
  const isPending = plan.status === 'pending';
  // RC BILLING.CANDIDATE-HARDENING-P0 — failed-payment lifecycle banner.
  // Distinct from `status` (doctrine). Sources from `lifecycle_status`:
  //   past_due     → amber banner, full service continues, "update card"
  //   delinquent   → rose banner, full service continues, urgent CTA
  //   usage_blocked → rose banner, new usage blocked
  const lifecycleStatus = plan.lifecycle_status || 'active';
  const showPastDueBanner       = lifecycleStatus === 'past_due';
  const showDelinquentBanner    = lifecycleStatus === 'delinquent';
  const showUsageBlockedBanner  = lifecycleStatus === 'usage_blocked';
  const showLifecycleBanner = (
    showPastDueBanner || showDelinquentBanner || showUsageBlockedBanner
  );
  // Recovery banner only renders inside the recovery window. After
  // 10 min the auto-cancel useEffect above clears the pending row
  // and the next /api/account/plan response will no longer report
  // status='pending' — so this block won't render again.
  const pendingSinceMs = (isPending && plan.pending_since)
    ? new Date(plan.pending_since).getTime()
    : null;
  const showPendingRecovery =
    isPending
    && pendingSinceMs !== null
    && !Number.isNaN(pendingSinceMs)
    && (Date.now() - pendingSinceMs) < PENDING_RECOVERY_WINDOW_MS;
  const statusColour =
    plan.status === 'active'  ? 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30' :
    plan.status === 'pending' ? 'bg-amber-500/15 text-amber-200 border-amber-500/30'   :
                                'bg-rose-500/15 text-rose-200 border-rose-500/30';
  const expiresIso = plan.access_expires_at;
  const expiresText = expiresIso
    ? new Date(expiresIso).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
      })
    : '—';

  // UI-01 — show the monthly cycle window for annual subscribers so
  // they can see when the next 120/250 included-usage refresh lands.
  // Hide it on monthly plans (cycle_ends_at == access_expires_at) and
  // on Base / expired states.
  const cycleEndsIso = plan.cycle_ends_at;
  const showCycleEnds =
    plan.billing_period === 'annual'
    && plan.status === 'active'
    && cycleEndsIso
    && cycleEndsIso !== expiresIso;
  const cycleEndsText = cycleEndsIso
    ? new Date(cycleEndsIso).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
      })
    : '';

  return (
    <div className="space-y-6" data-testid="account-plan-section">
      {showLifecycleBanner && (
        <div
          className={
            showPastDueBanner
              ? "flex flex-col sm:flex-row sm:items-start gap-3 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200"
              : "flex flex-col sm:flex-row sm:items-start gap-3 rounded-md border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200"
          }
          data-testid={`account-plan-lifecycle-${lifecycleStatus}-banner`}
        >
          <TriangleAlert className="h-4 w-4 shrink-0 mt-[2px]" />
          <div className="flex-1 space-y-2">
            <p className="font-medium">
              {showPastDueBanner   && 'Your last payment failed — please update your card to keep your plan active.'}
              {showDelinquentBanner && 'Your payment is overdue — service will be paused if not resolved.'}
              {showUsageBlockedBanner && 'New usage is blocked because your subscription is overdue. Update your card to restore access.'}
            </p>
            <button
              type="button"
              onClick={onManageBilling}
              data-testid={`account-plan-lifecycle-${lifecycleStatus}-portal-btn`}
              className="inline-flex items-center gap-2 rounded-md border border-current px-3 py-1.5 text-xs font-medium hover:bg-current/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
            >
              Update payment method
            </button>
          </div>
        </div>
      )}

      {isExpired && (
        <div
          className="flex items-start gap-2 rounded-md border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200"
          data-testid="account-plan-expired-banner"
        >
          <TriangleAlert className="h-4 w-4 shrink-0 mt-[2px]" />
          <span>Your plan has expired — renew to restore access.</span>
        </div>
      )}

      {showPendingRecovery && (
        <div
          className="flex flex-col sm:flex-row sm:items-start gap-3 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200"
          data-testid="account-plan-pending-recovery-banner"
        >
          <TriangleAlert className="h-4 w-4 shrink-0 mt-[2px]" />
          <div className="flex-1">
            <p className="font-medium">
              Your {PLAN_LABEL[plan.plan_code] || plan.plan_code} plan is pending payment.
            </p>
            <p className="mt-0.5 text-amber-200/80">
              Resume Stripe Checkout to activate it, or cancel the pending selection
              and stay on Base. We&apos;ll automatically clear it after 10 minutes
              if you don&apos;t return.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              size="sm"
              variant="outline"
              onClick={cancelPending}
              disabled={cancellingPending}
              data-testid="account-plan-pending-cancel-btn"
              className="border-amber-500/40 text-amber-200 hover:bg-amber-500/20"
            >
              {cancellingPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              Cancel pending plan
            </Button>
            <Button
              size="sm"
              onClick={() => navigate('/onboarding/payment')}
              data-testid="account-plan-pending-resume-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              Resume payment
            </Button>
          </div>
        </div>
      )}

      <header className="flex flex-col gap-1">
        <p className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-muted font-mono-ui">
          Your current plan
        </p>
        <div className="flex items-center flex-wrap gap-3">
          <h2
            className="text-3xl font-serif-display font-semibold text-ndor-ink tracking-tight"
            data-testid="account-plan-name"
          >
            {PLAN_LABEL[plan.plan_code] || plan.plan_code || 'Base'}
          </h2>
          <Badge
            variant="outline"
            className={`uppercase tracking-[0.12em] ${statusColour}`}
            data-testid="account-plan-status"
          >
            {STATUS_LABEL[plan.status] || plan.status}
          </Badge>
          {plan.billing_period && (
            <Badge
              variant="outline"
              className="text-ndor-ink-muted border-ndor-edge"
              data-testid="account-plan-billing-period"
            >
              {PERIOD_LABEL[plan.billing_period]}
            </Badge>
          )}
        </div>
        {plan.access_expires_at && (
          <p
            className="text-xs text-ndor-ink-muted"
            data-testid="account-plan-expires"
          >
            {isExpired ? 'Expired on ' : 'Renews '}{expiresText}
          </p>
        )}
        {/* RC 1.45.HARMONISE — Founding Member framing + outcome strapline,
            aligned with the marketing site and /onboarding/plan. Rendered
            only for paid plans with an active/pending status. */}
        {plan.plan_code && plan.plan_code !== 'base' && plan.status !== 'expired' && (
          <>
            <p
              className="text-[11px] uppercase tracking-[0.18em] text-ndor-gold font-mono-ui mt-1"
              data-testid="account-plan-founding-member"
            >
              Founding Member Pricing — locked in for life
            </p>
            <p
              className="text-[11px] text-ndor-ink-muted leading-snug mt-0.5"
              data-testid="account-plan-founding-member-context"
            >
              Your Founding Member rate is locked in below future pricing.
            </p>
            {PLAN_MONTHLY_BUNDLE[plan.plan_code] && perAppCost ? (
              <p
                className="text-xs text-ndor-ink-secondary leading-snug mt-1"
                data-testid="account-plan-app-count"
              >
                Supports multiple fully tailored applications per month.
              </p>
            ) : null}
          </>
        )}
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="rounded-md border border-ndor-edge bg-ndor-surface-alt/30 p-4">
          <p className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-muted font-mono-ui">
            Included Credits
          </p>
          <p
            className="text-2xl font-serif-display font-semibold text-ndor-ink tabular-nums"
            data-testid="account-plan-included-remaining"
          >
            {(plan.included_usage_remaining || 0).toLocaleString()}
          </p>
          <p className="text-xs text-ndor-ink-muted">
            Credits remaining this cycle
          </p>
          {showCycleEnds && (
            <p
              className="text-[11px] text-ndor-ink-muted mt-2"
              data-testid="account-plan-cycle-ends"
            >
              Cycle resets {cycleEndsText}
            </p>
          )}
        </div>
        <div className="rounded-md border border-ndor-edge bg-ndor-surface-alt/30 p-4">
          <p className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-muted font-mono-ui">
            Top-up Credits
          </p>
          <p
            className="text-2xl font-serif-display font-semibold text-ndor-ink tabular-nums"
            data-testid="account-plan-extra-remaining"
          >
            {(plan.extra_usage_remaining || 0).toLocaleString()}
          </p>
          <p className="text-xs text-ndor-ink-muted">
            {(plan.extra_usage_remaining || 0) === 0
              ? 'No top-up Credits on this account'
              : 'top-up Credits remaining'}
          </p>
          {/* RC 1.34 — Add Credits button. RC 1.35.F3 P1.18 — added
              stable `plan-add-credits-button` testid alongside the
              legacy `account-plan-add-usage` testid for backward
              compat. Payment is real Stripe TEST checkout (RC 1.35.F2
              Stage-2b). */}
          <Button
            variant="outline"
            size="sm"
            onClick={openSelector}
            data-testid="account-plan-add-usage"
            className="mt-3 border-ndor-edge text-ndor-ink hover:bg-ndor-surface"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            <span data-testid="plan-add-credits-button">Add Credits</span>
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        {/* RC 1.33.D — pass `state.from` so the plan page's Back / Cancel
            button knows to return the user here. */}
        <Link to="/onboarding/plan" state={{ from: '/account?tab=plan' }}>
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            data-testid="account-plan-change"
          >
            Change plan
          </Button>
        </Link>
        {isExpired && (
          <Link to="/onboarding/plan" state={{ from: '/account?tab=plan' }}>
            <Button
              className="w-full sm:w-auto bg-ndor-primary hover:bg-ndor-primary-hover text-ndor-primary-foreground"
              data-testid="account-plan-renew"
            >
              <CircleCheck className="h-4 w-4 mr-1.5" />
              Renew plan
            </Button>
          </Link>
        )}
        {/* RC 1.35.F3 P1.7 — Stripe Customer Portal deeplink. Backend
            mints a portal session for the user's stripe_customer_id;
            on success we redirect them to the Stripe-hosted UI for
            invoice history, card updates, and cancellation. */}
        <Button
          variant="outline"
          className="w-full sm:w-auto border-ndor-edge text-ndor-ink hover:bg-ndor-surface"
          onClick={onManageBilling}
          disabled={portalLoading}
          data-testid="customer-portal-button"
        >
          {portalLoading
            ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            : <ExternalLink className="h-4 w-4 mr-1.5" />}
          Manage billing & invoices
        </Button>
        <Link to="/credits" className="w-full sm:w-auto">
          <Button
            variant="ghost"
            className="w-full sm:w-auto text-ndor-ink-muted hover:text-ndor-ink"
            data-testid="billing-history-button"
          >
            <History className="h-4 w-4 mr-1.5" />
            View billing history
          </Button>
        </Link>
      </div>

      {/* RC 1.35.F3 — Trust micro-copy under the action row. */}
      <p
        className="text-[11px] text-ndor-ink-muted"
        data-testid="account-plan-trust-copy"
      >
        Secure payments by Stripe · Cancel anytime · Transparent billing ·
        Card details never touch NDORFlow
      </p>

      {/* ────────────────────────────────────────────────────────────
          RC 1.34 — Add-usage package selector (inline panel)
         ──────────────────────────────────────────────────────────── */}
      {step === 'select' && (
        <div
          className="rounded-xl border border-ndor-edge bg-ndor-surface-alt/30 p-5 space-y-4"
          data-testid="usage-package-selector"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-muted font-mono-ui">
                Add extra Credits
              </p>
              <h3 className="text-lg font-serif-display font-semibold text-ndor-ink">
                Choose a top-up
              </h3>
              <p className="text-xs text-ndor-ink-secondary mt-1">
                Top up extra Credits to use anywhere — base, Focus, or Flow.
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={closeFlow}
              data-testid="usage-package-selector-close"
              className="text-ndor-ink-muted hover:text-ndor-ink"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {pkgsLoading ? (
            <div className="flex items-center gap-2 text-sm text-ndor-ink-muted">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading packages…
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {packages.map((pkg) => (
                <button
                  key={pkg.code}
                  type="button"
                  onClick={() => chooseSelected(pkg)}
                  data-testid={`usage-package-${pkg.code}`}
                  className="text-left rounded-lg border border-ndor-edge bg-ndor-surface p-4 hover:border-ndor-gold/50 transition-colors"
                >
                  <p className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-muted font-mono-ui">
                    {pkg.label}
                  </p>
                  <p className="mt-1 text-2xl font-serif-display font-semibold text-ndor-ink tabular-nums">
                    {pkg.display_price}
                  </p>
                  <p className="mt-1 text-xs text-ndor-ink-secondary">
                    {pkg.usage_amount} top-up Credits · No expiry
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────
          RC 1.34 — Stub confirmation card
         ──────────────────────────────────────────────────────────── */}
      {(step === 'confirm' || step === 'activating') && selected && (
        <div
          className="rounded-xl border border-ndor-edge bg-ndor-surface-alt/30 p-5 space-y-4"
          data-testid="usage-package-confirm"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-muted font-mono-ui">
                Review & confirm
              </p>
              <h3
                className="text-lg font-serif-display font-semibold text-ndor-ink"
                data-testid="usage-package-confirm-title"
              >
                {selected.label} · {selected.display_price}
              </h3>
              <p
                className="text-xs text-ndor-ink-secondary mt-1"
                data-testid="usage-package-stub-notice"
              >
                {isStripeTestMode()
                  ? `You'll complete payment securely on Stripe (TEST MODE). When your top-up is confirmed, ${selected.usage_amount} extra Credits are added to your account.`
                  : `You'll complete payment securely on Stripe. When your top-up is confirmed, ${selected.usage_amount} extra Credits are added to your account.`}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={closeFlow}
              disabled={step === 'activating'}
              data-testid="usage-package-confirm-close"
              className="text-ndor-ink-muted hover:text-ndor-ink"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => setStep('select')}
              disabled={step === 'activating'}
              data-testid="usage-package-confirm-back"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              Back to packages
            </Button>
            <Button
              onClick={confirmActivate}
              disabled={step === 'activating'}
              data-testid="usage-package-confirm-activate"
              className="bg-ndor-primary hover:bg-ndor-primary-hover text-ndor-primary-foreground"
            >
              {step === 'activating'
                ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Redirecting to Stripe…</>
                : <>Pay with Stripe</>}
            </Button>
          </div>
        </div>
      )}

      {/* RC 1.61 S2.B.1 — institutional disclaimer (in-app Account Plan) */}
      <div className="mt-8">
        <Disclaimer variant="block" testId="account-plan-disclaimer" />
      </div>
    </div>
  );
}
